import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useContext, useState } from "react";
import { Emoji } from "emoji-picker-react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { PlannerContext } from "../../../../context/planner/PlannerContext";
import Button from "../../../input/button";
import { EmojiPicker, ColourPicker } from "../../../pickers";
import { PICKER_COLORS } from "../../../../constants/pickerColors";
import { getRandomColor } from "../../../../helpers/colorUtils";
import validateStartTime from "./validation/validateStartTime";
import validateEndTime from "./validation/validateEndTime";

const DayConfig = () => {
  const {
    day,
    saveAsTemplate,
    clearDay,
    currentTemplate,
    availableTemplates,
    sortedBlocks,
    closeToolPanel,
  } = useContext(PlannerContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { setStartTime, setEndTime, setTimeInterval } = day;
  const [startTimeInputText, setStartTimeInputText] = useState(day.startTime);
  const [endTimeInputText, setEndTimeInputText] = useState(day.endTime);
  const [timeIntervalInputValue, setTimeIntervalInputValue] = useState(
    day.timeInterval,
  );
  const [startTimeTouched, setStartTimeTouched] = useState(false);
  const [endTimeTouched, setEndTimeTouched] = useState(false);

  // Template save dialog state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateShortDescription, setTemplateShortDescription] = useState("");
  const [templateEmoji, setTemplateEmoji] = useState("");
  const [templateColor, setTemplateColor] = useState(
    getRandomColor(PICKER_COLORS),
  );

  // Clear day confirmation dialog state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const startTimeError = validateStartTime(
    startTimeInputText,
    endTimeInputText,
  );
  const endTimeError = validateEndTime(endTimeInputText, startTimeInputText);

  const onSave = () => {
    if (!startTimeError) {
      setStartTime(startTimeInputText);
    }
    if (!endTimeError) {
      setEndTime(endTimeInputText);
    }
    setTimeInterval(timeIntervalInputValue);
    if (isMobile) {
      closeToolPanel();
    }
  };

  const handleSaveTemplate = () => {
    if (templateName.trim()) {
      saveAsTemplate(
        templateName.trim(),
        templateEmoji,
        templateColor,
        templateShortDescription.trim(),
      );
      setSaveDialogOpen(false);
      setTemplateName("");
      setTemplateShortDescription("");
      setTemplateEmoji("");
      setTemplateColor(getRandomColor(PICKER_COLORS));
    }
  };

  const handleClearDay = async () => {
    await clearDay();
    setClearDialogOpen(false);
  };

  const currentTemplateData = currentTemplate
    ? availableTemplates[currentTemplate]
    : null;

  const hasBlocks = sortedBlocks.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        flex: 1,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
          pb: 2,
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="h3">Day Configuration</Typography>
          {currentTemplateData && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Active template:
              </Typography>
              <Chip
                size="small"
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Emoji
                      unified={currentTemplateData.emoji || "1f4cb"}
                      size={14}
                    />
                    <span>{currentTemplateData.name}</span>
                  </Box>
                }
                sx={{
                  backgroundColor: currentTemplateData.color
                    ? `${currentTemplateData.color}20`
                    : "grey.200",
                  borderColor: currentTemplateData.color || "grey.400",
                  border: "1px solid",
                  height: 24,
                  "& .MuiChip-label": {
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    px: 1,
                  },
                }}
              />
            </Box>
          )}
        </Box>

        {/* Schedule section */}
        <Box
          sx={{
            backgroundColor: "rgba(0, 74, 60, 0.05)",
            border: "1px solid rgba(0, 74, 60, 0.14)",
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
            <AccessTimeIcon sx={{ fontSize: 15, color: "text.secondary" }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                color: "text.secondary",
              }}
            >
              Schedule
            </Typography>
          </Box>

          {/* Start + End time side by side */}
          <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
            <TextField
              label="Start time"
              value={startTimeInputText}
              onChange={(e) => setStartTimeInputText(e.target.value)}
              error={startTimeTouched && !!startTimeError}
              helperText={startTimeTouched && startTimeError}
              onBlur={() => setStartTimeTouched(true)}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="End time"
              value={endTimeInputText}
              onChange={(e) => setEndTimeInputText(e.target.value)}
              error={endTimeTouched && !!endTimeError}
              helperText={endTimeTouched && endTimeError}
              onBlur={() => setEndTimeTouched(true)}
              size="small"
              sx={{ flex: 1 }}
            />
          </Box>

          <FormControl variant="outlined" fullWidth size="small">
            <InputLabel id="time-interval-label">Time interval</InputLabel>
            <Select
              labelId="time-interval-label"
              variant="outlined"
              label="Time interval"
              value={timeIntervalInputValue}
              onChange={(e) => setTimeIntervalInputValue(e.target.value)}
            >
              <MenuItem value={15}>15 minutes</MenuItem>
              <MenuItem value={30}>30 minutes</MenuItem>
              <MenuItem value={60}>1 hour</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {!isMobile && (
          <Box sx={{ mb: 2.5 }}>
            <Button
              text="Apply Settings"
              onClick={onSave}
              fullWidth
              color="orange"
              disabled={!!startTimeError || !!endTimeError}
            />
          </Box>
        )}

        {/* Templates section */}
        <Box
          sx={{
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            border: "1px solid rgba(0, 0, 0, 0.09)",
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
            <BookmarkBorderIcon sx={{ fontSize: 15, color: "text.secondary" }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                color: "text.secondary",
              }}
            >
              Templates
            </Typography>
          </Box>

          <Button
            text="Save as Template"
            onClick={() => setSaveDialogOpen(true)}
            color="primary"
            fullWidth
            disabled={!hasBlocks}
            icon="bookmark"
          />
          {!hasBlocks && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Add blocks to your day before saving as a template
            </Typography>
          )}
        </Box>

        {/* Danger zone */}
        <Box
          sx={{
            backgroundColor: "rgba(203, 85, 56, 0.04)",
            border: "1px solid rgba(203, 85, 56, 0.18)",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.6,
              color: "error.main",
              opacity: 0.7,
              display: "block",
              mb: 1.5,
            }}
          >
            Danger Zone
          </Typography>
          <Button
            text="Start Fresh"
            onClick={() => setClearDialogOpen(true)}
            color="red"
            fullWidth
            icon="refresh"
          />
        </Box>
      </Box>

      {isMobile && (
        <Box
          sx={{
            pt: 2,
            pb: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            flexShrink: 0,
          }}
        >
          <Button
            text="Apply Settings"
            onClick={onSave}
            fullWidth
            color="orange"
            disabled={!!startTimeError || !!endTimeError}
          />
        </Box>
      )}

      {/* Save Template Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1301 }} // Nested — above ToolPanel dialog (1300) on mobile
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle>Save as Template</DialogTitle>
        <DialogContent
          sx={{
            flex: 1,
            overflow: "auto",
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pt: 0.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Give your template a name and choose an emoji and color to
              identify it.
            </Typography>

            <TextField
              autoFocus
              label="Template Name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              fullWidth
              size="small"
            />

            <TextField
              label="Short Description (optional)"
              value={templateShortDescription}
              onChange={(e) => setTemplateShortDescription(e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g., For productive weekdays"
            />

            {/* Emoji + Color row */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
              {/* Emoji selector */}
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.75, display: "block", fontWeight: 600 }}
                >
                  Emoji
                </Typography>
                <EmojiPicker emoji={templateEmoji} setEmoji={setTemplateEmoji} />
              </Box>

              {/* Color selector */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.75, display: "block", fontWeight: 600 }}
                >
                  Colour
                </Typography>
                <ColourPicker
                  selectedColor={templateColor}
                  onColorSelect={setTemplateColor}
                  size={32}
                />
              </Box>
            </Box>

            {/* Preview */}
            <Box
              sx={{
                backgroundColor: "rgba(0,0,0,0.03)",
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 2,
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                Preview:
              </Typography>
              <Chip
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {templateEmoji ? (
                      <Emoji unified={templateEmoji} size={16} />
                    ) : (
                      <Emoji unified="1f4cb" size={16} />
                    )}
                    <span>{templateName || "Template Name"}</span>
                  </Box>
                }
                sx={{
                  backgroundColor: `${templateColor}20`,
                  borderColor: templateColor,
                  border: "1px solid",
                  "& .MuiChip-label": {
                    fontWeight: 500,
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Button
            text="Cancel"
            onClick={() => setSaveDialogOpen(false)}
            color="grey"
          />
          <Button
            text="Save"
            onClick={handleSaveTemplate}
            color="orange"
            disabled={!templateName.trim()}
          />
        </DialogActions>
      </Dialog>

      {/* Clear Day Confirmation Dialog */}
      <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)}>
        <DialogTitle>Start Fresh?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will clear all blocks from your day. This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            text="Cancel"
            onClick={() => setClearDialogOpen(false)}
            color="grey"
          />
          <Button text="Clear Day" onClick={handleClearDay} color="red" />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DayConfig;
